import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import Close from "@mui/icons-material/Close";
import Description from "@mui/icons-material/Description";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";

export interface ExportColumnConfig {
  key: string;
  label: string;
  formatter?: (value: any, row: any) => string | number;
}

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  data: Record<string, any>[];
  columns: ExportColumnConfig[];
  defaultFileName: string;
  sheetName?: string;
  onBeforeWrite?: (
    workbook: XLSX.WorkBook,
    worksheet: XLSX.WorkSheet,
  ) => void;
}

export function ExportDialog({
  open,
  onClose,
  title,
  data,
  columns,
  defaultFileName,
  sheetName,
  onBeforeWrite,
}: ExportDialogProps) {
  const [columnList, setColumnList] = useState<ExportColumnConfig[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [fileName, setFileName] = useState(defaultFileName);

  useEffect(() => {
    if (open) {
      setColumnList([...columns]);
      setSelectedKeys(columns.map((c) => c.key));
      setFileName(defaultFileName.replace(/\.xlsx$/, ""));
    }
  }, [open, columns, defaultFileName]);

  const allSelected =
    selectedKeys.length === columns.length && columns.length > 0;

  const moveUp = (index: number) => {
    if (index === 0) return;
    const next = [...columnList];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    setColumnList(next);
  };

  const moveDown = (index: number) => {
    if (index === columnList.length - 1) return;
    const next = [...columnList];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    setColumnList(next);
  };

  const handleExport = () => {
    const visible = columnList.filter((c) => selectedKeys.includes(c.key));

    const rows = data.map((row) => {
      const obj: Record<string, any> = {};
      visible.forEach((col) => {
        const value = row[col.key];
        obj[col.label] = col.formatter ? col.formatter(value, row) : value;
      });
      return obj;
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName || "Dados");

    if (onBeforeWrite) {
      onBeforeWrite(workbook, worksheet);
    }

    XLSX.writeFile(workbook, `${fileName}.xlsx`, { compression: true });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
          borderBottom: "1px solid #eef2f6",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Description sx={{ color: "#1e293b", fontSize: 22 }} />
          <Typography
            sx={{ fontWeight: 700, color: "#1e293b", fontSize: 18 }}
          >
            {title}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#94a3b8" }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3.5, pb: 2.5 }}>
        <TextField
          fullWidth
          size="small"
          label="Nome do arquivo"
          value={fileName}
          onChange={(e) => setFileName(e.target.value)}
          sx={{ mt: 2.5, mb: 2.5 }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
            px: 0.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#1e293b" }}
          >
            Colunas para exportar
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontWeight: 500 }}
            >
              {selectedKeys.length} de {columns.length} selecionadas
            </Typography>
            <Button
              size="small"
              onClick={() => {
                if (allSelected) {
                  setSelectedKeys([]);
                } else {
                  setSelectedKeys(columns.map((c) => c.key));
                }
              }}
              sx={{
                minWidth: "auto",
                height: 26,
                fontSize: 12,
                fontWeight: 600,
                textTransform: "none",
                color: "#475569",
                border: "1px solid #e2e8f0",
                borderRadius: 1.5,
                px: 1.5,
                "&:hover": { bgcolor: "#f8fafc" },
              }}
            >
              {allSelected ? "Desmarcar todos" : "Marcar todos"}
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            maxHeight: 340,
            overflow: "auto",
            borderRadius: 2,
            border: "1px solid #eef2f6",
          }}
        >
          {columnList.map((col, index) => {
            const isSelected = selectedKeys.includes(col.key);
            return (
              <Box
                key={col.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  borderBottom: "1px solid #f1f5f9",
                  bgcolor: isSelected ? "#ffffff" : "#fafbfc",
                  "&:last-child": { borderBottom: "none" },
                  transition: "background 0.15s",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  sx={{
                    color: "#94a3b8",
                    p: 0.3,
                    "&.Mui-disabled": { opacity: 0.2 },
                  }}
                >
                  <ArrowUpward sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => moveDown(index)}
                  disabled={index === columnList.length - 1}
                  sx={{
                    color: "#94a3b8",
                    p: 0.3,
                    "&.Mui-disabled": { opacity: 0.2 },
                  }}
                >
                  <ArrowDownward sx={{ fontSize: 16 }} />
                </IconButton>
                <Switch
                  checked={isSelected}
                  onChange={() => {
                    if (isSelected) {
                      setSelectedKeys((prev) =>
                        prev.filter((k) => k !== col.key),
                      );
                    } else {
                      setSelectedKeys((prev) => [...prev, col.key]);
                    }
                  }}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#24324D",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      bgcolor: "#24324D",
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: isSelected ? "#1e293b" : "#94a3b8",
                    fontWeight: isSelected ? 500 : 400,
                    userSelect: "none",
                    flex: 1,
                  }}
                >
                  {col.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid #eef2f6",
          gap: 1.5,
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#e2e8f0",
            color: "#475569",
            fontWeight: 600,
            borderRadius: 2,
            px: 3,
            "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={selectedKeys.length === 0}
          sx={{
            bgcolor: "#24324D",
            fontWeight: 600,
            borderRadius: 2,
            px: 4,
            py: 1,
            "&:hover": { bgcolor: "#1a2536" },
            "&.Mui-disabled": { bgcolor: "#cbd5e1" },
          }}
        >
          Exportar Excel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
